import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  StatusPill,
  Textarea,
  Toggle,
  Skeleton,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import { useConfirm, useToast } from '../../components/admin/overlays';
import { IconAlert, IconCheck, IconInfo, IconPlus } from '../../components/admin/icons';
import { PLAN_OPTIONS, planLabel } from '../../components/admin/plans';

const planKeys = PLAN_OPTIONS.map((p) => p.value);

const TAB_STORAGE_KEY = 'pb-admin-pricing-tab';

/**
 * Tabs group the settings by what they govern. `keys` lists the SystemConfig
 * keys each tab owns, which is what drives the unsaved-changes dot — otherwise
 * a pending edit on a hidden tab would be invisible.
 */
/**
 * Pay-per-case is no longer sold — merchants are on the pilot or the per-user
 * plan. Its tab is hidden rather than removed: the tiers it edits still price
 * merchants who were put on pay-per-case historically, and the billing code
 * that reads them is untouched. Flip this to bring the tab back.
 */
const SHOW_PAY_PER_CASE = false;

const ALL_TABS = [
  { id: 'plans', label: 'Merchant plans', keys: ['merchantPlanPrices', 'seatPrice', 'seatStripePriceId'] },
  { id: 'courierPlans', label: 'Courier plans', keys: ['courierPlanPrices'] },
  { id: 'payPerCase', label: 'Pay per case', keys: ['payPerCaseTiers'] },
  { id: 'sms', label: 'SMS', keys: ['smsPrice', 'smsEnabled'] },
  {
    id: 'tracking',
    label: 'Tracking',
    keys: ['ship24Enabled', 'ship24CacheDuration', 'delayTimerDays', 'ship24IssueTypes']
  }
];

const TABS = ALL_TABS.filter((t) => t.id !== 'payPerCase' || SHOW_PAY_PER_CASE);

const defaultPlans = {
  pilot: { price: 0, includedCases: 7, includedUsers: 1, overThresholdFee: null, isTrial: true, features: [] },
  merchant: { price: 67, includedCases: null, includedUsers: 1, overThresholdFee: null, stripePriceId: null, features: [] }
};

const defaultCourierPlans = {
  pilot: { price: 0, freeCaseAllowance: 7, isTrial: true, features: [] },
  intelligence: { price: 499, stripePriceId: null, features: [] }
};

/** Selling points are edited as one per line and stored as an array. */
const featuresToText = (features) => (Array.isArray(features) ? features.join('\n') : '');
const textToFeatures = (text) =>
  String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const defaultPayPerCaseTiers = [
  { min: 1, max: 10, pricePerCase: 4 },
  { min: 11, max: 50, pricePerCase: 3 },
  { min: 51, max: 200, pricePerCase: 2 },
  { min: 201, max: null, pricePerCase: 1.5 }
];

/** Ship24 is called per issue type by fixed rules; only one of them is tunable. */
const ship24Rules = [
  { tone: 'success', label: 'Delivered but not received', detail: 'Always called immediately' },
  { tone: 'warning', label: 'Not arrived yet', detail: 'Called once the delay timer expires' },
  { tone: 'neutral', label: 'Damaged', detail: 'Never called — courier status is irrelevant' },
  { tone: 'neutral', label: 'Missing items', detail: 'Never called — order content issue' },
  { tone: 'info', label: 'Left in unsafe place', detail: 'Optional — configurable below' }
];

const numberOrEmpty = (value) => {
  if (value === '' || value == null) return '';
  const n = Number(value);
  return isNaN(n) ? '' : n;
};

const PricingManagement = () => {
  const confirm = useConfirm();
  const toast = useToast();

  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState({});
  const [tab, setTab] = useState(() => {
    const stored = localStorage.getItem(TAB_STORAGE_KEY);
    return TABS.some((t) => t.id === stored) ? stored : 'plans';
  });

  const selectTab = (id) => {
    setTab(id);
    localStorage.setItem(TAB_STORAGE_KEY, id);
  };

  const fetchConfig = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/config');
      setConfig(response.data);
    } catch (error) {
      toast('Could not load configuration.', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleChange = (key, value) => {
    setChanges((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async (key, value, { label, skipConfirm = false } = {}) => {
    if (!skipConfirm) {
      const ok = await confirm({
        title: 'Save configuration',
        message: `Update ${label || key}? This takes effect immediately for all merchants.`,
        confirmLabel: 'Save changes'
      });
      if (!ok) return;
    }

    setSaving(true);
    try {
      await api.put(`/api/admin/config/${key}`, { value });
      setConfig((current) => ({ ...current, [key]: value }));
      setChanges((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      toast(`${label || key} updated.`, { tone: 'success' });
    } catch (error) {
      toast('Error updating configuration.', { tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const smsPrice = changes.smsPrice !== undefined ? changes.smsPrice : config.smsPrice ?? 0.99;
  const seatPrice = changes.seatPrice !== undefined ? changes.seatPrice : config.seatPrice ?? 67;
  const seatStripePriceId =
    changes.seatStripePriceId !== undefined
      ? changes.seatStripePriceId
      : config.seatStripePriceId ?? '';

  const rawPlans =
    changes.merchantPlanPrices !== undefined
      ? changes.merchantPlanPrices
      : config.merchantPlanPrices || defaultPlans;

  const rawCourierPlans =
    changes.courierPlanPrices !== undefined
      ? changes.courierPlanPrices
      : config.courierPlanPrices || defaultCourierPlans;

  const rawPayPerCaseTiers =
    changes.payPerCaseTiers !== undefined
      ? changes.payPerCaseTiers
      : config.payPerCaseTiers && config.payPerCaseTiers.length
      ? config.payPerCaseTiers
      : defaultPayPerCaseTiers;

  function toPlanRow(v) {
    if (v == null) {
      return { price: '', includedCases: '', includedUsers: '', overThresholdFee: '', isCustom: false, isTrial: false, stripePriceId: '', features: [] };
    }
    if (typeof v === 'number') {
      return { price: v, includedCases: '', includedUsers: '', overThresholdFee: '', isCustom: false, isTrial: false, stripePriceId: '', features: [] };
    }
    return {
      price: v.price ?? '',
      includedCases: v.includedCases ?? '',
      includedUsers: v.includedUsers ?? '',
      overThresholdFee: v.overThresholdFee ?? '',
      isCustom: Boolean(v.isCustom),
      isTrial: Boolean(v.isTrial),
      stripePriceId: v.stripePriceId ?? '',
      features: Array.isArray(v.features) ? v.features : []
    };
  }

  function updatePlan(plan, field, value) {
    const row = toPlanRow(rawPlans[plan]);
    if (field === 'features') row.features = textToFeatures(value);
    else if (field === 'stripePriceId') row.stripePriceId = value;
    else row[field] = numberOrEmpty(value);
    handleChange('merchantPlanPrices', { ...rawPlans, [plan]: row });
  }

  /**
   * Flags are set separately from the numeric fields: updatePlan coerces every
   * value through numberOrEmpty, which would turn a boolean into a number.
   *
   * Marking a plan quote-only also clears its price, so a stale figure cannot
   * sit behind the "Custom" label and resurface if the flag is turned off.
   */
  function updatePlanFlag(plan, field, checked) {
    const row = toPlanRow(rawPlans[plan]);
    row[field] = Boolean(checked);
    if (field === 'isCustom' && checked) row.price = '';
    handleChange('merchantPlanPrices', { ...rawPlans, [plan]: row });
  }

  function sanitizePlansForSave() {
    const out = {};
    planKeys.forEach((plan) => {
      const r = toPlanRow(rawPlans[plan]);
      out[plan] = {
        price: r.isCustom ? null : Number(r.price) || 0,
        includedCases: r.includedCases === '' ? null : Number(r.includedCases),
        includedUsers: r.includedUsers === '' ? null : Number(r.includedUsers),
        overThresholdFee: r.overThresholdFee === '' ? null : Number(r.overThresholdFee),
        stripePriceId: r.stripePriceId ? r.stripePriceId.trim() : null,
        features: r.features,
        ...(r.isCustom && { isCustom: true }),
        ...(r.isTrial && { isTrial: true })
      };
    });
    return out;
  }

  const courierPlanKeys = ['pilot', 'intelligence'];

  function toCourierPlanRow(v) {
    if (v == null) return { price: '', freeCaseAllowance: '', stripePriceId: '', isTrial: false, features: [] };
    return {
      price: v.price ?? '',
      freeCaseAllowance: v.freeCaseAllowance ?? '',
      stripePriceId: v.stripePriceId ?? '',
      isTrial: Boolean(v.isTrial),
      features: Array.isArray(v.features) ? v.features : []
    };
  }

  function updateCourierPlan(plan, field, value) {
    const row = toCourierPlanRow(rawCourierPlans[plan]);
    if (field === 'features') row.features = textToFeatures(value);
    else if (field === 'stripePriceId') row.stripePriceId = value;
    else row[field] = numberOrEmpty(value);
    handleChange('courierPlanPrices', { ...rawCourierPlans, [plan]: row });
  }

  function sanitizeCourierPlansForSave() {
    const out = {};
    courierPlanKeys.forEach((plan) => {
      const r = toCourierPlanRow(rawCourierPlans[plan]);
      out[plan] = {
        price: Number(r.price) || 0,
        freeCaseAllowance: r.freeCaseAllowance === '' ? null : Number(r.freeCaseAllowance),
        stripePriceId: r.stripePriceId ? r.stripePriceId.trim() : null,
        features: r.features,
        ...(r.isTrial && { isTrial: true })
      };
    });
    return out;
  }

  function updatePayPerCaseTier(index, field, value) {
    const tiers = rawPayPerCaseTiers.map((t) => ({ ...t }));
    const t = tiers[index] || { min: 1, max: null, pricePerCase: 0 };
    if (field === 'min') {
      const n = value === '' ? '' : parseInt(value, 10);
      t.min = n === '' || isNaN(n) ? 1 : Math.max(1, n);
    } else if (field === 'max') {
      if (value === '' || value === null) {
        t.max = null;
      } else {
        const n = parseInt(value, 10);
        t.max = isNaN(n) ? null : Math.max(0, n);
      }
    } else if (field === 'pricePerCase') {
      const n = value === '' ? '' : parseFloat(value);
      t.pricePerCase = n === '' || isNaN(n) ? 0 : Math.max(0, n);
    }
    tiers[index] = t;
    handleChange('payPerCaseTiers', tiers);
  }

  function addPayPerCaseTier() {
    const last = rawPayPerCaseTiers[rawPayPerCaseTiers.length - 1];
    const nextMin = last ? (last.max != null ? last.max + 1 : last.min + 1) : 1;
    handleChange('payPerCaseTiers', [
      ...rawPayPerCaseTiers,
      { min: nextMin, max: null, pricePerCase: 1.5 }
    ]);
  }

  function removePayPerCaseTier(index) {
    const tiers = rawPayPerCaseTiers.filter((_, i) => i !== index);
    handleChange('payPerCaseTiers', tiers.length ? tiers : defaultPayPerCaseTiers);
  }

  function sanitizePayPerCaseTiersForSave() {
    const out = rawPayPerCaseTiers
      .map((t) => {
        const min = typeof t.min === 'number' && !isNaN(t.min) ? Math.max(1, t.min) : 1;
        const max =
          t.max === '' || t.max === undefined
            ? null
            : typeof t.max === 'number' && !isNaN(t.max)
            ? Math.max(0, t.max)
            : null;
        const pricePerCase =
          typeof t.pricePerCase === 'number' && !isNaN(t.pricePerCase) ? Math.max(0, t.pricePerCase) : 0;
        return { min, max: max || null, pricePerCase };
      })
      .sort((a, b) => a.min - b.min);
    return out.length ? out : defaultPayPerCaseTiers;
  }

  /* ── Tabs ─────────────────────────────────────────────────────────────────── */

  const tabIsDirty = (t) => t.keys.some((k) => changes[k] !== undefined);

  const onTabKeyDown = (e, index) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = TABS[(index + dir + TABS.length) % TABS.length];
    selectTab(next.id);
    document.getElementById(`pricing-tab-${next.id}`)?.focus();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full max-w-md" />
        {[0, 1].map((key) => (
          <Card key={key}>
            <CardBody>
              <Skeleton className="h-5 w-48" />
              <Skeleton className="mt-4 h-10 w-full max-w-md" />
              <Skeleton className="mt-3 h-10 w-full max-w-sm" />
            </CardBody>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab bar */}
      <div className="border-b border-ink-200">
        <div role="tablist" aria-label="Pricing settings" className="-mb-px flex flex-wrap gap-1">
          {TABS.map((t, index) => {
            const active = tab === t.id;
            const dirty = tabIsDirty(t);
            return (
              <button
                key={t.id}
                id={`pricing-tab-${t.id}`}
                role="tab"
                type="button"
                aria-selected={active}
                aria-controls={`pricing-panel-${t.id}`}
                tabIndex={active ? 0 : -1}
                onClick={() => selectTab(t.id)}
                onKeyDown={(e) => onTabKeyDown(e, index)}
                className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
                  active
                    ? 'border-brand-800 font-600 text-brand-800'
                    : 'border-transparent font-medium text-ink-500 hover:border-ink-300 hover:text-ink-900'
                }`}
              >
                {t.label}
                {dirty && (
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-warning-600"
                    title="Unsaved changes on this tab"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Plans & seats */}
      {tab === 'plans' && (
        <div id="pricing-panel-plans" role="tabpanel" aria-labelledby="pricing-tab-plans" className="space-y-6">
          <Card>
            <CardHeader
              title="Merchant plans"
              description="Monthly price, the case allowance, and how many user seats each plan includes."
              actions={
                changes.merchantPlanPrices && (
                  <Button
                    onClick={() =>
                      handleSave('merchantPlanPrices', sanitizePlansForSave(), { label: 'plan pricing' })
                    }
                    loading={saving}
                    size="sm"
                  >
                    <IconCheck size={15} />
                    Save all plans
                  </Button>
                )
              }
            />
            <CardBody className="space-y-5">
              {planKeys.map((plan) => {
                const row = toPlanRow(rawPlans[plan]);
                return (
                  <div
                    key={plan}
                    className="rounded-xl border border-ink-200 p-4 transition-colors hover:border-ink-300"
                  >
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-sm font-700 text-ink-900">{planLabel(plan)}</h3>
                      {row.isCustom ? (
                        <StatusPill tone="neutral" dot={false}>Quote only</StatusPill>
                      ) : (
                        <StatusPill tone="neutral" dot={false}>
                          £{Number(row.price || 0).toFixed(2)}/mo
                        </StatusPill>
                      )}
                      {row.isTrial && <StatusPill tone="info" dot={false}>Trial</StatusPill>}
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                      <Field label="Price (£/month)" htmlFor={`${plan}-price`}>
                        <Input
                          id={`${plan}-price`}
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={row.isCustom ? 'Custom' : '0.00'}
                          disabled={row.isCustom}
                          value={row.price}
                          onChange={(e) => updatePlan(plan, 'price', e.target.value)}
                        />
                      </Field>
                      <Field label="Cases (blank = unlimited)" htmlFor={`${plan}-cases`}>
                        <Input
                          id={`${plan}-cases`}
                          type="number"
                          min="0"
                          placeholder="Unlimited"
                          value={row.includedCases}
                          onChange={(e) => updatePlan(plan, 'includedCases', e.target.value)}
                        />
                      </Field>
                      <Field label="Included users" htmlFor={`${plan}-users`}>
                        <Input
                          id={`${plan}-users`}
                          type="number"
                          min="0"
                          placeholder="—"
                          value={row.includedUsers}
                          onChange={(e) => updatePlan(plan, 'includedUsers', e.target.value)}
                        />
                      </Field>
                      <Field label="Over-allowance (£)" htmlFor={`${plan}-over`}>
                        <Input
                          id={`${plan}-over`}
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="—"
                          value={row.overThresholdFee}
                          onChange={(e) => updatePlan(plan, 'overThresholdFee', e.target.value)}
                        />
                      </Field>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field
                        label="Stripe Price ID (optional)"
                        htmlFor={`${plan}-stripe-price`}
                        hint="If set, checkout uses this Stripe Price instead of a dynamically-built price."
                      >
                        <Input
                          id={`${plan}-stripe-price`}
                          type="text"
                          placeholder="price_..."
                          disabled={row.isCustom}
                          value={row.stripePriceId}
                          onChange={(e) => updatePlan(plan, 'stripePriceId', e.target.value)}
                        />
                      </Field>
                      <Field
                        label="Selling points"
                        htmlFor={`${plan}-features`}
                        hint="One per line. Shown on the pricing page, the activation modal and the portal upgrade cards."
                      >
                        <Textarea
                          id={`${plan}-features`}
                          rows={6}
                          placeholder={'Full dashboard case management\nView and action all cases'}
                          value={featuresToText(row.features)}
                          onChange={(e) => updatePlan(plan, 'features', e.target.value)}
                        />
                      </Field>
                    </div>

                    {/*
                      Without this the flag could only be set in the database:
                      every other control here writes a number, and a plan with
                      no price but no flag reads as "Free" on the pricing page
                      and in the activation modal alike.
                    */}
                    <label
                      htmlFor={`${plan}-custom`}
                      className="mt-3 inline-flex items-center gap-2.5 text-sm text-ink-700"
                    >
                      <input
                        id={`${plan}-custom`}
                        type="checkbox"
                        checked={row.isCustom}
                        onChange={(e) => updatePlanFlag(plan, 'isCustom', e.target.checked)}
                        className="h-4 w-4 rounded border-ink-300 text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                      />
                      <span>
                        Quote only
                        <span className="ml-1.5 text-ink-500">
                          — shows “Custom / Talk to us” instead of a price
                        </span>
                      </span>
                    </label>
                  </div>
                );
              })}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Additional user seats"
              description="Charged per extra user per month, on top of the seats a plan already includes — this is also what makes the Merchant plan per-user: its one included seat costs the plan price, and every seat after that costs this."
            />
            <CardBody>
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Seat price (£/user/month)" htmlFor="seat-price">
                  <Input
                    id="seat-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={seatPrice}
                    onChange={(e) => handleChange('seatPrice', parseFloat(e.target.value))}
                    className="w-40"
                  />
                </Field>
                {changes.seatPrice !== undefined && (
                  <Button
                    onClick={() => handleSave('seatPrice', seatPrice, { label: 'seat price' })}
                    loading={saving}
                  >
                    <IconCheck size={16} />
                    Save
                  </Button>
                )}
              </div>

              <div className="mt-5 flex flex-wrap items-end gap-3 border-t border-ink-100 pt-5">
                <Field
                  label="Stripe Price ID (optional)"
                  htmlFor="seat-price-id"
                  hint="Leave empty to have ParcelBuddy create a Price per merchant from the amount above. Set one to bill every seat against a single Price you manage in Stripe."
                  className="min-w-[20rem] flex-1"
                >
                  <Input
                    id="seat-price-id"
                    value={seatStripePriceId}
                    onChange={(e) => handleChange('seatStripePriceId', e.target.value)}
                    placeholder="price_1234…"
                    className="font-mono text-xs"
                  />
                </Field>
                {changes.seatStripePriceId !== undefined && (
                  <Button
                    onClick={() =>
                      handleSave('seatStripePriceId', seatStripePriceId.trim() || null, {
                        label: 'seat Stripe Price ID'
                      })
                    }
                    loading={saving}
                  >
                    <IconCheck size={16} />
                    Save
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Courier plans */}
      {tab === 'courierPlans' && (
        <div id="pricing-panel-courierPlans" role="tabpanel" aria-labelledby="pricing-tab-courierPlans">
          <Card>
            <CardHeader
              title="Courier provider plans"
              description="Pricing for courier-provider business accounts — a separate ladder from the merchant plans above."
              actions={
                changes.courierPlanPrices && (
                  <Button
                    onClick={() =>
                      handleSave('courierPlanPrices', sanitizeCourierPlansForSave(), { label: 'courier plan pricing' })
                    }
                    loading={saving}
                    size="sm"
                  >
                    <IconCheck size={15} />
                    Save all plans
                  </Button>
                )
              }
            />
            <CardBody className="space-y-5">
              {courierPlanKeys.map((plan) => {
                const row = toCourierPlanRow(rawCourierPlans[plan]);
                return (
                  <div
                    key={plan}
                    className="rounded-xl border border-ink-200 p-4 transition-colors hover:border-ink-300"
                  >
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-sm font-700 text-ink-900">
                        {plan === 'pilot' ? 'Pilot' : 'Intelligence'}
                      </h3>
                      <StatusPill tone="neutral" dot={false}>
                        £{Number(row.price || 0).toFixed(2)}/mo
                      </StatusPill>
                      {row.isTrial && <StatusPill tone="info" dot={false}>Trial</StatusPill>}
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <Field label="Price (£/month)" htmlFor={`courier-${plan}-price`}>
                        <Input
                          id={`courier-${plan}-price`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={row.price}
                          onChange={(e) => updateCourierPlan(plan, 'price', e.target.value)}
                        />
                      </Field>
                      {plan === 'pilot' ? (
                        <Field label="Free cases" htmlFor={`courier-${plan}-cases`}>
                          <Input
                            id={`courier-${plan}-cases`}
                            type="number"
                            min="0"
                            value={row.freeCaseAllowance}
                            onChange={(e) => updateCourierPlan(plan, 'freeCaseAllowance', e.target.value)}
                          />
                        </Field>
                      ) : (
                        <Field
                          label="Stripe Price ID (optional)"
                          htmlFor={`courier-${plan}-stripe-price`}
                          hint="If set, checkout uses this Stripe Price instead of a dynamically-built price."
                        >
                          <Input
                            id={`courier-${plan}-stripe-price`}
                            type="text"
                            placeholder="price_..."
                            value={row.stripePriceId}
                            onChange={(e) => updateCourierPlan(plan, 'stripePriceId', e.target.value)}
                          />
                        </Field>
                      )}
                    </div>

                    <div className="mt-4">
                      <Field
                        label="Selling points"
                        htmlFor={`courier-${plan}-features`}
                        hint="One per line. Shown on the pricing page, the courier site and the portal upgrade card."
                      >
                        <Textarea
                          id={`courier-${plan}-features`}
                          rows={5}
                          placeholder={'Full intelligence dashboard\nPostcode hotspot analysis'}
                          value={featuresToText(row.features)}
                          onChange={(e) => updateCourierPlan(plan, 'features', e.target.value)}
                        />
                      </Field>
                    </div>
                  </div>
                );
              })}
            </CardBody>
          </Card>
        </div>
      )}

      {/* Pay per case */}
      {tab === 'payPerCase' && (
        <div id="pricing-panel-payPerCase" role="tabpanel" aria-labelledby="pricing-tab-payPerCase">
          <Card>
            <CardHeader
              title="Pay-per-case pricing"
              description="Fee per case by volume tier — e.g. cases 1–10 at £4, 11–50 at £3. Leave the upper bound empty for no limit."
              actions={
                changes.payPerCaseTiers !== undefined && (
                  <Button
                    onClick={() =>
                      handleSave('payPerCaseTiers', sanitizePayPerCaseTiersForSave(), {
                        label: 'pay-per-case pricing'
                      })
                    }
                    loading={saving}
                    size="sm"
                  >
                    <IconCheck size={15} />
                    Save tiers
                  </Button>
                )
              }
            />
            <TableWrap>
              <thead>
                <tr>
                  <Th>Min cases</Th>
                  <Th>Max cases</Th>
                  <Th>Price per case (£)</Th>
                  <Th className="w-24 text-right">Remove</Th>
                </tr>
              </thead>
              <tbody>
                {rawPayPerCaseTiers.map((tier, index) => (
                  <Tr key={index}>
                    <Td>
                      <Input
                        type="number"
                        min="1"
                        aria-label={`Tier ${index + 1} minimum cases`}
                        value={tier.min ?? ''}
                        onChange={(e) => updatePayPerCaseTier(index, 'min', e.target.value)}
                        className="w-28"
                      />
                    </Td>
                    <Td>
                      <Input
                        type="number"
                        min="0"
                        placeholder="No limit"
                        aria-label={`Tier ${index + 1} maximum cases`}
                        value={tier.max ?? ''}
                        onChange={(e) =>
                          updatePayPerCaseTier(index, 'max', e.target.value === '' ? '' : e.target.value)
                        }
                        className="w-32"
                      />
                    </Td>
                    <Td>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        aria-label={`Tier ${index + 1} price per case`}
                        value={tier.pricePerCase ?? ''}
                        onChange={(e) => updatePayPerCaseTier(index, 'pricePerCase', e.target.value)}
                        className="w-32"
                      />
                    </Td>
                    <Td className="text-right">
                      <Button
                        variant="dangerGhost"
                        size="sm"
                        onClick={() => removePayPerCaseTier(index)}
                        disabled={rawPayPerCaseTiers.length <= 1}
                      >
                        Remove
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </TableWrap>
            <div className="px-5 py-4">
              <Button variant="secondary" size="sm" onClick={addPayPerCaseTier}>
                <IconPlus size={15} />
                Add tier
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* SMS */}
      {tab === 'sms' && (
        <div id="pricing-panel-sms" role="tabpanel" aria-labelledby="pricing-tab-sms" className="space-y-6">
          <Card>
            <CardHeader
              title="SMS pricing"
              description="What a customer pays to receive SMS updates on a case."
            />
            <CardBody>
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Price per case (£)" htmlFor="sms-price">
                  <Input
                    id="sms-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={smsPrice}
                    onChange={(e) => handleChange('smsPrice', parseFloat(e.target.value))}
                    className="w-36"
                  />
                </Field>
                {changes.smsPrice !== undefined && (
                  <Button
                    onClick={() => handleSave('smsPrice', smsPrice, { label: 'SMS price' })}
                    loading={saving}
                  >
                    <IconCheck size={16} />
                    Save
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="SMS controls" description="System-wide switch for customer SMS updates." />
            <CardBody>
              <Toggle
                id="sms-enabled"
                label="SMS enabled globally"
                description="When off, no SMS is sent regardless of customer opt-in."
                checked={changes.smsEnabled !== undefined ? changes.smsEnabled : config.smsEnabled !== false}
                onChange={(checked) => {
                  handleChange('smsEnabled', checked);
                  handleSave('smsEnabled', checked, { label: 'SMS enabled', skipConfirm: true });
                }}
              />
            </CardBody>
          </Card>
        </div>
      )}

      {/* Tracking */}
      {tab === 'tracking' && (
        <div id="pricing-panel-tracking" role="tabpanel" aria-labelledby="pricing-tab-tracking">
          <Card>
            <CardHeader
              title="Ship24 controls"
              description="Tracking lookups, cache lifetime and the delay timer for cases that have not arrived."
            />
            <CardBody className="space-y-6">
              <Toggle
                id="ship24-enabled"
                label="Ship24 enabled globally"
                description="When off, no tracking lookups are made for any case."
                checked={
                  changes.ship24Enabled !== undefined ? changes.ship24Enabled : config.ship24Enabled !== false
                }
                onChange={(checked) => {
                  handleChange('ship24Enabled', checked);
                  handleSave('ship24Enabled', checked, { label: 'Ship24 enabled', skipConfirm: true });
                }}
              />

              <div className="grid grid-cols-1 gap-4 border-t border-ink-100 pt-6 sm:grid-cols-2">
                <div className="flex flex-wrap items-end gap-3">
                  <Field label="Cache duration (days)" htmlFor="ship24-cache">
                    <Input
                      id="ship24-cache"
                      type="number"
                      min="1"
                      value={
                        changes.ship24CacheDuration !== undefined
                          ? changes.ship24CacheDuration
                          : config.ship24CacheDuration || 30
                      }
                      onChange={(e) => handleChange('ship24CacheDuration', parseInt(e.target.value, 10))}
                      className="w-32"
                    />
                  </Field>
                  {changes.ship24CacheDuration !== undefined && (
                    <Button
                      size="sm"
                      loading={saving}
                      onClick={() =>
                        handleSave('ship24CacheDuration', changes.ship24CacheDuration, {
                          label: 'cache duration'
                        })
                      }
                    >
                      Save
                    </Button>
                  )}
                </div>

                <div className="flex flex-wrap items-end gap-3">
                  <Field label="Delay timer (days)" htmlFor="ship24-delay">
                    <Input
                      id="ship24-delay"
                      type="number"
                      min="1"
                      value={
                        changes.delayTimerDays !== undefined
                          ? changes.delayTimerDays
                          : config.delayTimerDays || 7
                      }
                      onChange={(e) => handleChange('delayTimerDays', parseInt(e.target.value, 10))}
                      className="w-32"
                    />
                  </Field>
                  {changes.delayTimerDays !== undefined && (
                    <Button
                      size="sm"
                      loading={saving}
                      onClick={() =>
                        handleSave('delayTimerDays', changes.delayTimerDays, { label: 'delay timer' })
                      }
                    >
                      Save
                    </Button>
                  )}
                </div>
              </div>

              <div className="border-t border-ink-100 pt-6">
                <div className="mb-3 flex items-center gap-2">
                  <IconInfo size={16} className="text-info-600" />
                  <h3 className="font-display text-sm font-700 text-ink-900">
                    When Ship24 is called, by issue type
                  </h3>
                </div>
                <ul className="space-y-2">
                  {ship24Rules.map((rule) => (
                    <li
                      key={rule.label}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-ink-50 px-3.5 py-2.5"
                    >
                      <span className="text-sm font-medium text-ink-800">{rule.label}</span>
                      <StatusPill tone={rule.tone}>{rule.detail}</StatusPill>
                    </li>
                  ))}
                </ul>

                <div className="mt-5 rounded-xl border border-ink-200 p-4">
                  <Toggle
                    id="ship24-unsafe"
                    label="Call Ship24 for “left in unsafe place”"
                    description="The only issue type whose tracking behaviour is configurable."
                    checked={
                      changes.ship24IssueTypes?.unsafe_place !== undefined
                        ? changes.ship24IssueTypes.unsafe_place
                        : config.ship24IssueTypes?.unsafe_place || false
                    }
                    onChange={(checked) => {
                      const currentTypes = changes.ship24IssueTypes || config.ship24IssueTypes || {};
                      handleChange('ship24IssueTypes', { ...currentTypes, unsafe_place: checked });
                    }}
                  />
                  {changes.ship24IssueTypes && (
                    <div className="mt-4 flex items-center gap-3 border-t border-ink-100 pt-4">
                      <IconAlert size={16} className="shrink-0 text-warning-600" />
                      <p className="flex-1 text-xs text-ink-600">Unsaved change to issue type settings.</p>
                      <Button
                        size="sm"
                        loading={saving}
                        onClick={() =>
                          handleSave('ship24IssueTypes', changes.ship24IssueTypes, {
                            label: 'issue type settings'
                          })
                        }
                      >
                        Save
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PricingManagement;
